import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Ticket,
  FileText,
  Users,
  BarChart3,
  Activity,
  Headset,
  UserCog,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useUser } from "../../context/UserContext";
import { useTicketContext } from "../../context/TicketContext";

// Visible to everyone, including external partners.
// `activePrefixes` covers related sub-routes (e.g. a ticket detail page should
// still highlight "Support Tickets") — matched via pathname.startsWith().
const BASE_ITEMS = [
  { label: "Support Tickets", path: "/Dashboard", icon: Ticket, activePrefixes: ["/Dashboard", "/Form", "/Ticket"] },
  // Assistant Status (LOA) is visible to internal AND partner accounts.
  { label: "Assistant Status", path: "/AssistantStatus", icon: FileText, activePrefixes: ["/AssistantStatus"] },
];

// Internal-only, shown right under Assistant Status. RCS external-client tickets queue.
const RCS_SUPPORT_ITEM = {
  label: "Sandesh NNI Support",
  path: "/RcsSupport",
  icon: Headset,
  activePrefixes: ["/RcsSupport"],
};

// Internal-only — hidden from partner_account / org_admin users.
const INTERNAL_ITEMS = [
  { label: "Resources", path: "/Resources", icon: Users, activePrefixes: ["/Resource"] },
  { label: "Analytics", path: "/Analytics", icon: BarChart3, activePrefixes: ["/Analytics"] },
  { label: "Workload", path: "/Workload", icon: Activity, activePrefixes: ["/Workload"] },
];

// Platform-admin only.
const ADMIN_ITEMS = [
  { label: "User Management", path: "/Admin/Users", icon: UserCog, activePrefixes: ["/Admin"] },
];

const COLLAPSED_STORAGE_KEY = "sidebarCollapsed";

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_STORAGE_KEY) === "true",
  );
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const { ticketClientType } = useTicketContext();
  const isInternal = user.isPartner === false;
  const isAdmin = !!user.role?.platform_admin;

  // A ticket detail lives at the shared /Ticket/:id route, so use the open
  // ticket's clientType to decide which section is "active".
  const onTicketDetail = location.pathname.startsWith("/Ticket");
  const rcsTicketOpen = onTicketDetail && ticketClientType === "RCS";

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(COLLAPSED_STORAGE_KEY, String(next));
      return next;
    });
  };

  const isActive = (item) => {
    // RCS Support: its own routes, or an open RCS ticket detail.
    if (item.path === "/RcsSupport") {
      return location.pathname.startsWith("/RcsSupport") || rcsTicketOpen;
    }
    // Support Tickets: its prefixes, but not when the open ticket is RCS.
    if (item.path === "/Dashboard" && rcsTicketOpen) {
      return false;
    }
    return item.activePrefixes.some((prefix) => location.pathname.startsWith(prefix));
  };

  const renderItem = (item) => {
    const { label, path, icon: Icon } = item;
    const active = isActive(item);
    return (
      <div
        key={path}
        onClick={() => navigate(path)}
        title={collapsed ? label : undefined}
        className={`flex items-center gap-3 px-6 py-3 cursor-pointer font-medium text-sm border-r-4 ${
          active
            ? "border-jiojbm50 text-jiojbm50 bg-jiojbm20"
            : "border-transparent text-gray-600 hover:bg-gray-50"
        } ${collapsed ? "justify-center px-0" : ""}`}
      >
        <Icon size={18} className="flex-shrink-0" />
        {!collapsed && <span>{label}</span>}
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col bg-white border-r border-gray-200 h-[calc(100vh-72px)] transition-all duration-200 ${
        collapsed ? "w-16" : "w-56"
      }`}
    >
      <div className="flex-1 py-5 overflow-y-auto overflow-x-hidden">
        {BASE_ITEMS.map(renderItem)}
        {isInternal && renderItem(RCS_SUPPORT_ITEM)}

        {isInternal && (
          <>
            {!collapsed && (
              <div className="text-[11px] font-bold text-gray-400 tracking-wide uppercase mt-5 mb-1 px-6">
                Internal Ops
              </div>
            )}
            {collapsed && <div className="my-3 mx-3 border-t border-gray-200" />}
            {INTERNAL_ITEMS.map(renderItem)}
          </>
        )}

        {isInternal && isAdmin && (
          <>
            {!collapsed && (
              <div className="text-[11px] font-bold text-gray-400 tracking-wide uppercase mt-5 mb-1 px-6">
                Admin
              </div>
            )}
            {collapsed && <div className="my-3 mx-3 border-t border-gray-200" />}
            {ADMIN_ITEMS.map(renderItem)}
          </>
        )}
      </div>

      <button
        type="button"
        onClick={toggleCollapsed}
        className="flex items-center justify-center gap-2 py-3 text-xs font-bold text-gray-500 border-t border-gray-200 hover:bg-gray-50"
      >
        {collapsed ? <ChevronRight size={16} /> : <><ChevronLeft size={16} /> Collapse</>}
      </button>
    </div>
  );
};
